import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { COMPANY } from "../lib/company";
import { useT } from "../lib/i18n";
import LanguageSwitcher from "./LanguageSwitcher";

const LEGAL_LINKS = [
  { to: "/termeni-si-conditii", key: "legal.terms" },
  { to: "/politica-de-confidentialitate", key: "legal.privacy" },
  { to: "/politica-de-cookies", key: "legal.cookies" },
  { to: "/politica-de-retur", key: "legal.return" },
  { to: "/politica-de-livrare", key: "legal.shipping" },
  { to: "/politica-de-garantie", key: "legal.warranty" },
  { to: "/informatii-plati", key: "legal.payments" },
];

export default function Footer() {
  const { t } = useT();
  return (
    <footer className="border-t border-gray-200 mt-32 bg-white" data-testid="site-footer">
      {/* Language selector – top of footer */}
      <div className="border-b border-gray-200 bg-gray-50/40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <LanguageSwitcher variant="footer" />
          <p className="text-[11px] text-gray-500">{t("footer.language.note")}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-2 md:grid-cols-12 gap-10">
        {/* Brand + Contact */}
        <div className="col-span-2 md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="inline-block w-9 h-9 relative">
              <span className="absolute inset-0 bg-gray-900" />
              <span className="absolute inset-[3px] bg-white" />
              <span className="absolute inset-[6px] bg-gray-900" />
            </span>
            <span className="font-display text-xl font-black tracking-tighter">ANAIZA · SHOP</span>
          </div>
          <p className="mt-5 text-sm text-gray-500 max-w-md leading-relaxed">{t("footer.tagline")}</p>
          <div className="mt-6 flex flex-col gap-2.5 text-sm">
            <a href={`mailto:${COMPANY.contact.email}`} className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 group" data-testid="footer-email">
              <Mail size={16} strokeWidth={1.5} className="text-gray-500 group-hover:text-gray-900" />
              <span className="font-mono">{COMPANY.contact.email}</span>
            </a>
            <a href={`tel:${COMPANY.contact.phone_tel}`} className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 group" data-testid="footer-phone">
              <Phone size={16} strokeWidth={1.5} className="text-gray-500 group-hover:text-gray-900" />
              <span className="font-mono">{COMPANY.contact.phone}</span>
            </a>
            <a
              href={`https://wa.me/${COMPANY.contact.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#1ebe57] hover:text-[#188f42]"
              data-testid="footer-whatsapp"
            >
              <MessageCircle size={16} strokeWidth={1.5} />
              <span className="font-semibold">{t("footer.whatsapp")}</span>
            </a>
          </div>
        </div>

        {/* Magazin links */}
        <div className="md:col-span-2">
          <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-4">{t("footer.shop")}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/categorie/cutite" className="text-gray-700 hover:text-gray-900">{t("nav.knives")}</Link></li>
            <li><Link to="/categorie/scule" className="text-gray-700 hover:text-gray-900">{t("nav.tools")}</Link></li>
            <li><Link to="/cont" className="text-gray-700 hover:text-gray-900">{t("footer.account")}</Link></li>
            <li><Link to="/contact" className="text-gray-700 hover:text-gray-900">{t("footer.contact")}</Link></li>
          </ul>
        </div>

        {/* Legal links */}
        <div className="col-span-2 md:col-span-5">
          <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-4">{t("footer.legal")}</h4>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {LEGAL_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-gray-700 hover:text-gray-900 transition" data-testid={`footer-legal-${l.to.replace(/\//g, '')}`}>{t(l.key)}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Date Firmă */}
      <div className="border-t border-gray-200 bg-gray-50/60">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-7 grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600" data-testid="footer-company-info">
          <div>
            <p className="font-bold uppercase tracking-[0.2em] text-gray-400 mb-1.5">{t("footer.company.name")}</p>
            <p className="text-gray-900 font-semibold">{COMPANY.legal_name}</p>
          </div>
          <div>
            <p className="font-bold uppercase tracking-[0.2em] text-gray-400 mb-1.5">{t("footer.company.cif")}</p>
            <p className="text-gray-900 font-mono">{COMPANY.cif}</p>
          </div>
          <div>
            <p className="font-bold uppercase tracking-[0.2em] text-gray-400 mb-1.5">{t("footer.company.reg")}</p>
            <p className="text-gray-900 font-mono">{COMPANY.reg_com}</p>
          </div>
          <div>
            <p className="font-bold uppercase tracking-[0.2em] text-gray-400 mb-1.5">{t("footer.company.hq")}</p>
            <p className="text-gray-900 inline-flex items-start gap-1 leading-snug">
              <MapPin size={12} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
              <span>{COMPANY.address.street}, {COMPANY.address.city}, {COMPANY.address.county}</span>
            </p>
          </div>
        </div>
      </div>

      {/* ANPC badges */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-4 md:gap-5" data-testid="anpc-badges">
            <a
              href="https://anpc.ro/ce-este-sal/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="block border border-gray-200 hover:border-gray-400 bg-white p-2 transition-colors"
              aria-label="ANPC SAL"
              data-testid="anpc-sal"
            >
              <img src="/anpc-sal.svg" alt="ANPC SAL" className="h-10 md:h-11 w-auto" />
            </a>
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="block border border-gray-200 hover:border-gray-400 bg-white p-2 transition-colors"
              aria-label="ANPC SOL"
              data-testid="anpc-sol"
            >
              <img src="/anpc-sol.svg" alt="ANPC SOL" className="h-10 md:h-11 w-auto" />
            </a>
          </div>
          <p className="text-[10px] md:text-xs text-gray-500 leading-relaxed max-w-xl">
            ANAIZA SHOP · OUG 140/2021 · ANPC SAL & SOL
          </p>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row justify-between text-xs text-gray-500 gap-2">
          <span>© {new Date().getFullYear()} {COMPANY.legal_name}. {t("footer.copyright")}</span>
          <span className="font-mono">{t("footer.signature")}</span>
        </div>
      </div>
    </footer>
  );
}
